import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { motion } from "framer-motion";

import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";

const C = {
  dark: "#0C1F2F",
  navy: "#0057A8",
  navyDark: "#003D73",
  gold: "#FFCD00",
  goldDark: "#E6B800",
  teal: "#5BC8AF",
  gray: "#F5F7FA",
  white: "#ffffff",
};

const CONTACT_ITEMS = [
  {
    icon: <LocationOnIcon sx={{ color: C.gold, fontSize: 18 }} />,
    label: "Ubicación",
    lines: ["Quito, Ecuador."],
  },
  {
    icon: <PhoneIcon sx={{ color: C.gold, fontSize: 18 }} />,
    label: "Llámanos",
    lines: ["098 772 4998"],
  },
  {
    icon: <EmailIcon sx={{ color: C.gold, fontSize: 18 }} />,
    label: "Email",
    lines: [
      "contabilidad@electricagrm.com",
      "gerencia@electricagrm.com",
      "ventas1@electricagrm.com",
    ],
  },
];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    "&.Mui-focused fieldset": { borderColor: C.navy },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: C.navy },
};

export default function ContactoEstructura() {
  const handleSubmit = (e) => {
    e.preventDefault();
    alert("¡Gracias por tu mensaje! Nos pondremos en contacto pronto.");
  };

  return (
    /* Sin franja ni gradiente superior/inferior — el fondo del layout lo maneja la página */
    <Box
      id="contacto"
      component={motion.section}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.7 }}
      sx={{
        background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyDark} 100%)`,
        pt: { xs: "100px", md: "120px" },
        pb: { xs: "60px", md: "80px" },
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          width: "100%",
          px: { xs: "20px", sm: "32px", md: "40px" },
          boxSizing: "border-box",
        }}
      >
        {/* Encabezado */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          sx={{ textAlign: "center", mb: { xs: 5, md: 8 } }}
        >
          <Typography
            variant="h2"
            sx={{
              color: C.white,
              fontWeight: 800,
              fontSize: { xs: 26, sm: 32, md: 42 },
              letterSpacing: "-.02em",
              lineHeight: 1.15,
              mb: 1.5,
            }}
          >
            ¡Te ayudaremos en todo lo que podamos!
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.70)",
              fontSize: { xs: 13.5, md: 16 },
              maxWidth: "520px",
              mx: "auto",
              lineHeight: 1.65,
            }}
          >
            Déjanos tu consulta y pronto estaremos en contacto.
          </Typography>
        </Box>

        {/* Tarjeta */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          sx={{
            background: C.white,
            borderRadius: "24px",
            boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
            overflow: "hidden",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          {/* Lado izquierdo */}
          <Box
            sx={{
              background: `linear-gradient(160deg, ${C.dark} 0%, ${C.navyDark} 100%)`,
              color: C.white,
              p: { xs: "32px 24px", md: "48px 40px" },
              width: { xs: "100%", md: "40%" },
              display: "flex",
              flexDirection: "column",
              position: "relative",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                bottom: "-60px",
                right: "-60px",
                width: "220px",
                height: "220px",
                borderRadius: "50%",
                background: `${C.gold}10`,
                pointerEvents: "none",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: "-40px",
                left: "-40px",
                width: "160px",
                height: "160px",
                borderRadius: "50%",
                background: `${C.teal}08`,
                pointerEvents: "none",
              }}
            />

            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: 22, md: 28 },
                  mb: 1.5,
                  letterSpacing: "-.01em",
                }}
              >
                Contáctanos
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.68)",
                  mb: { xs: 4, md: 5 },
                  fontSize: { xs: 13.5, md: 15 },
                  lineHeight: 1.7,
                }}
              >
                Estamos aquí para ayudarte en todo lo que necesites. ¡No dudes
                en escribirnos!
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: { xs: 3, md: 4 },
                }}
              >
                {CONTACT_ITEMS.map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      display: "flex",
                      gap: "16px",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "10px",
                        background: "rgba(255,205,0,0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box>
                      <Typography
                        sx={{ fontWeight: 800, fontSize: 13.5, mb: 0.4 }}
                      >
                        {item.label}
                      </Typography>
                      {item.lines.map((line) => (
                        <Typography
                          key={line}
                          sx={{
                            color: "rgba(255,255,255,0.68)",
                            fontSize: { xs: 12.5, md: 14 },
                            lineHeight: 1.6,
                            wordBreak: "break-word",
                          }}
                        >
                          {line}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Lado derecho — formulario */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              p: { xs: "32px 24px", md: "48px 40px" },
              width: { xs: "100%", md: "60%" },
              display: "flex",
              flexDirection: "column",
              gap: { xs: "18px", md: "22px" },
              boxSizing: "border-box",
            }}
          >
            <Typography
              sx={{
                color: C.dark,
                fontWeight: 800,
                fontSize: { xs: 20, md: 24 },
              }}
            >
              Envíanos un mensaje
            </Typography>
            <TextField
              label="Nombre completo"
              variant="outlined"
              fullWidth
              required
              sx={fieldSx}
            />
            <TextField
              label="Email"
              type="email"
              variant="outlined"
              fullWidth
              required
              sx={fieldSx}
            />
            <TextField
              label="Mensaje"
              variant="outlined"
              fullWidth
              required
              multiline
              rows={4}
              sx={fieldSx}
            />
            <Button
              type="submit"
              variant="contained"
              endIcon={<SendIcon />}
              sx={{
                background: C.gold,
                color: C.dark,
                fontWeight: 800,
                fontSize: "14px",
                py: "14px",
                borderRadius: "50px",
                textTransform: "none",
                boxShadow: "0 10px 24px rgba(255,205,0,0.35)",
                mt: 1,
                transition: "background .2s, transform .15s, box-shadow .2s",
                "&:hover": {
                  background: C.goldDark,
                  transform: "translateY(-2px)",
                  boxShadow: "0 16px 30px rgba(255,205,0,0.40)",
                },
                "&:active": { transform: "translateY(0)" },
              }}
            >
              Enviar Mensaje
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
